import React, { useState, useEffect } from "react";
import { attach } from "@adobe/uix-guest";
import {
  Provider,
  defaultTheme,
  Flex,
  Text,
  Button,
  ButtonGroup,
  ProgressCircle,
  Checkbox,
} from "@adobe/react-spectrum";
import CheckmarkCircle from "@spectrum-icons/workflow/CheckmarkCircle";
import CloseCircle from "@spectrum-icons/workflow/CloseCircle";
import LockClosed from "@spectrum-icons/workflow/LockClosed";
import { extensionId } from "./Constants";
import invokeExternalService from "./InvokeExternalService";

const PHASE = {
  AUTHORIZING: "authorizing",
  DENIED: "denied",
  CONFIRM: "confirm",
  LOADING: "loading",
  RESULT: "result",
};

const ALLOWED_EMAIL_DOMAIN = "adobe.com";

function InvokeServiceModal() {
  const [phase, setPhase] = useState(PHASE.AUTHORIZING);
  const [guestConnection, setGuestConnection] = useState(null);
  const [resultMessage, setResultMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [deniedMessage, setDeniedMessage] = useState("");

  useEffect(() => {
    (async () => {
      const connection = await attach({ id: extensionId });
      setGuestConnection(connection);
      await authorizeUser(connection);
    })();
  }, []);

  const authorizeUser = async (connection) => {
    try {
      const token = connection?.sharedContext?.get("token");
      if (!token) {
        setDeniedMessage("Unable to verify your identity. No Adobe IMS token was provided.");
        setPhase(PHASE.DENIED);
        return;
      }

      const profileResponse = await fetch(
        "https://ims-na1.adobelogin.com/ims/profile/v1",
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!profileResponse.ok) {
        setDeniedMessage(
          `Unable to verify your identity with Adobe IMS (status ${profileResponse.status}).`
        );
        setPhase(PHASE.DENIED);
        return;
      }

      const profile = await profileResponse.json();
      const email = (profile.email || "").toLowerCase();
      const emailDomain = email.split("@")[1];

      if (emailDomain !== ALLOWED_EMAIL_DOMAIN) {
        setDeniedMessage(
          `Access denied. This action is restricted to Interal Adobe users.`
        );
        setPhase(PHASE.DENIED);
        return;
      }

      setPhase(PHASE.CONFIRM);
    } catch (error) {
      console.error("[InvokeServiceModal] Authorization check failed →", error);
      setDeniedMessage("An unexpected error occurred while verifying your identity.");
      setPhase(PHASE.DENIED);
    }
  };

  const closeModal = () => {
    guestConnection?.host?.modal?.close();
  };

  const handleConfirm = async () => {
    setPhase(PHASE.LOADING);
    try {
      const result = await invokeExternalService(guestConnection);
      console.log("[InvokeServiceModal] Service result →", result);
      setIsSuccess(true);
      setResultMessage("The external service executed successfully.");
    } catch (error) {
      console.error("[InvokeServiceModal] Service error →", error);
      setIsSuccess(false);
      setResultMessage("An unexpected error occurred while executing the service.");
    }
    setPhase(PHASE.RESULT);
  };

  return (
    <Provider theme={defaultTheme} UNSAFE_style={{ overflow: "hidden" }}>
      <Flex
        direction="column"
        alignItems="center"
        justifyContent="center"
        gap="size-150"
        UNSAFE_style={{ padding: "16px 24px", height: "100%", boxSizing: "border-box" }}
      >
        {phase === PHASE.AUTHORIZING && (
          <Flex alignItems="center" gap="size-150">
            <ProgressCircle aria-label="Verifying access…" isIndeterminate size="S" />
            <Text>Verifying access…</Text>
          </Flex>
        )}

        {phase === PHASE.DENIED && (
          <>
            <Flex alignItems="center" gap="size-100">
              <LockClosed size="S" color="negative" />
              <Text UNSAFE_style={{ fontWeight: 600 }}>Access denied</Text>
            </Flex>
            <Text UNSAFE_style={{ textAlign: "center", fontSize: "13px" }}>
              {deniedMessage}
            </Text>
            <Button variant="accent" marginTop="size-50" onPress={closeModal}>
              Close
            </Button>
          </>
        )}

        {phase === PHASE.CONFIRM && (
          <>
            <Text UNSAFE_style={{ textAlign: "center" }}>
              Are you sure?
            </Text>
            <ButtonGroup marginTop="size-100">
              <Button variant="secondary" onPress={closeModal}>No</Button>
              <Button variant="accent" onPress={handleConfirm}>Yes</Button>
            </ButtonGroup>
          </>
        )}

        {phase === PHASE.LOADING && (
          <Flex alignItems="center" gap="size-150">
            <ProgressCircle aria-label="Executing…" isIndeterminate size="S" />
            <Text>Executing external service…</Text>
          </Flex>
        )}

        {phase === PHASE.RESULT && (
          <>
            <Flex alignItems="center" gap="size-100">
              {isSuccess ? (
                <CheckmarkCircle size="S" color="positive" />
              ) : (
                <CloseCircle size="S" color="negative" />
              )}
              <Text UNSAFE_style={{ fontWeight: 600 }}>
                {isSuccess ? "Success" : "Failed"}
              </Text>
            </Flex>
            <Text UNSAFE_style={{ textAlign: "center", fontSize: "13px" }}>
              {resultMessage}
            </Text>
            <Button variant="accent" marginTop="size-50" onPress={closeModal}>
              Close
            </Button>
          </>
        )}
      </Flex>
    </Provider>
  );
}

export default InvokeServiceModal;
